import { Control, FieldErrors, useForm } from "react-hook-form";
import { KnowledgeBasesV2Service, KnowledgeBaseV2 } from "@/generated";
import { FormEventHandler, useState } from "react";
import { useSpotUser } from "@/utils/spotUser";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { SpotToast } from "../SpotToast";
import { DATA_KEYS } from "@/utils/useRemoteData";
import { NoxusInputField } from "../Form/InputField";
import BaseDialog from "../Modals/BaseDialog";
import { NoxusIconPickerField } from "../Flows/NoxusIconPicker";
import { usePermissions } from "@/hooks/usePermissions";
import { WORKSPACE_PERMISSIONS } from "@/constants/permissions";
import {
  MAX_ENTITY_NAME,
  MAX_ENTITY_DESCRIPTION,
} from "@/constants/entityLimits";

type IKBEditFormData = {
  title: string;
  description: string;
  icon: string;
};

export const KBDetailsDialog = ({
  kb,
  isOpen,
  onOpenChange,
}: {
  kb: KnowledgeBaseV2;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const {
    control,
    handleSubmit,
    formState: { errors, isDirty, isValid },
  } = useForm({
    defaultValues: {
      title: kb.name || "",
      description: kb.description || "",
      icon: (kb.meta?.icon as string) || "kb",
    },
  });

  const [isLoading, setIsLoading] = useState(false);
  const { spotGroup } = useSpotUser();
  const { hasPermission } = usePermissions();
  const canEditKB = hasPermission(WORKSPACE_PERMISSIONS.EDIT, "group");

  const queryClient = useQueryClient();
  const editKB = useMutation({
    mutationFn: KnowledgeBasesV2Service.updateKnowledgeBaseV2,
    onSuccess: () => {
      SpotToast.success("Knowledge base updated successfully");
      queryClient.invalidateQueries({
        queryKey: [DATA_KEYS.KNOWLEDGE_BASES_V2_ID],
      });
      queryClient.invalidateQueries({
        queryKey: [DATA_KEYS.KNOWLEDGE_BASES_V2],
      });
      onOpenChange(false);
    },
    onError: () => {
      SpotToast.error("Error updating knowledge base");
    },
    onSettled: () => {
      setIsLoading(false);
    },
  });

  const handleFormSubmit = (data: IKBEditFormData) => {
    setIsLoading(true);

    const body: any = {};

    // Only include fields that have changed
    if (data.title !== kb.name) {
      body.name = data.title === "" ? "Untitled knowledge base" : data.title;
    }

    if (data.description !== kb.description) {
      body.description = data.description;
    }

    const currentIcon = (kb.meta?.icon as string) || "kb";
    if (data.icon !== currentIcon) {
      body.meta = { icon: data.icon };
    }

    editKB.mutate({
      path: {
        knowledge_base_id: kb.id,
        group_id: spotGroup.id,
      },
      body,
    });
  };

  const footerActions = [
    {
      label: "Cancel",
      props: { variant: "outline", onClick: () => onOpenChange(false) },
    },
    {
      label: "Save",
      props: {
        onClick: handleSubmit(handleFormSubmit),
        loading: isLoading,
        disabled: isLoading || !isDirty || !isValid,
      },
    },
  ];

  return (
    <BaseDialog
      title="Knowledge base details"
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      footerActions={footerActions}
    >
      <div>
        <KBEditForm
          control={control}
          errors={errors}
          isEditable={canEditKB}
          forcedName={kb.name}
          onSubmit={handleSubmit(handleFormSubmit)}
        />
      </div>
    </BaseDialog>
  );
};

interface IKbEditFormData {
  title: string;
  description: string;
  icon: string;
}

const KBEditForm = ({
  forcedName,
  isEditable = true,
  control,
  errors,
  onSubmit,
}: {
  forcedName?: string;
  isEditable?: boolean;
  control: Control<any>;
  errors: FieldErrors<any>;
  onSubmit: FormEventHandler<HTMLFormElement>;
}) => {
  return (
    <form className="w-full" id="kb-edit-form" onSubmit={onSubmit}>
      <div className="flex flex-col gap-6 items-start">
        <NoxusIconPickerField
          name="icon"
          label="Icon"
          size="md"
          control={control}
          disable={!isEditable}
        />
        <NoxusInputField
          name="title"
          maxLength={MAX_ENTITY_NAME}
          label="Title"
          errors={errors}
          forcedName={forcedName || "Untitled knowledge base"}
          disable={!isEditable}
          control={control}
          rules={{
            required: { value: true, message: "" },
          }}
          autoFocus
        />
        <NoxusInputField
          name="description"
          label="Description"
          maxLength={MAX_ENTITY_DESCRIPTION}
          helperText="Doesn't affect the knowledge base's behavior but helps other tools interpret it more accurately."
          isTextarea
          isResizable={false}
          disable={!isEditable}
          errors={errors}
          control={control}
        />
      </div>
    </form>
  );
};
